import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Bell, XCircle, AlertTriangle, Info, CheckCircle, Activity, Calendar, RefreshCw } from "lucide-react";
import { alertesAPI, cascadeAPI } from "../api/services";
import { formatDateTime } from "../utils/dateUtils";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";

// ── Mapping type → style ────────────────────────────────────
const typeStyles = {
  Critique:      { icon: XCircle,       iconBg: "bg-red-100",    iconColor: "text-red-500",    badgeBg: "bg-red-500" },
  Avertissement: { icon: AlertTriangle, iconBg: "bg-yellow-100", iconColor: "text-yellow-500", badgeBg: "bg-yellow-500" },
  Information:   { icon: Info,          iconBg: "bg-blue-100",   iconColor: "text-blue-500",   badgeBg: "bg-blue-500" },
  Succès:        { icon: CheckCircle,   iconBg: "bg-green-100",  iconColor: "text-green-500",  badgeBg: "bg-green-500" },
};

function tempsRelatif(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const min = Math.floor((now - date) / 60000);
  if (min < 1)  return "Il y a 0 min";
  if (min < 60) return `Il y a ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return `Il y a ${h}h${m > 0 ? m + "min" : ""}`;
  const j = Math.floor(h / 24);
  return `Il y a ${j}j`;
}

const formatDate = formatDateTime;

export default function Alertes() {
  const { t } = useTranslation();
  const { adminMode } = useAuth();
  const [search, setSearch]         = useState("");
  const [filterRev, setFilterRev]   = useState("");
  const [filterCli, setFilterCli]   = useState("");
  const [filterProj, setFilterProj] = useState("");
  const [filterType, setFilterType] = useState("Tout");
  const [alertes, setAlertes]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const { lastAlert } = useSocket();

  // Ajouter les nouvelles alertes en temps réel
  useEffect(() => {
    if (!lastAlert) return;
    setAlertes(prev => [lastAlert, ...prev]);
  }, [lastAlert]);

  // Cascade filter options
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients]       = useState([]);
  const [projets, setProjets]       = useState([]);

  // Fetch alertes
  const fetchAlertes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== "Tout") params.type = filterType;
      if (filterRev) params.revendeur = filterRev;
      if (filterCli) params.client = filterCli;
      if (filterProj) params.projet = filterProj;
      if (search) params.search = search;
      const res = await alertesAPI.getAll(params);
      setAlertes(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Erreur chargement alertes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlertes(); }, [filterType, filterRev, filterCli, filterProj, search]);

  // Load cascade revendeurs on mount
  useEffect(() => {
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data?.data || res.data || [])).catch(() => {});
  }, []);

  // Load clients when revendeur changes (all clients if no revendeur selected)
  useEffect(() => {
    cascadeAPI.clients(filterRev || undefined).then(res => setClients(res.data?.data || res.data || [])).catch(() => {});
  }, [filterRev]);

  // Load projets when client changes
  useEffect(() => {
    cascadeAPI.projets(filterCli || undefined, filterRev || undefined).then(res => setProjets(res.data?.data || res.data || [])).catch(() => {});
  }, [filterCli, filterRev]);

  const handleRev = (v) => { setFilterRev(v); setFilterCli(""); setFilterProj(""); };
  const handleCli = (v) => { setFilterCli(v); setFilterProj(""); };
  const reset = () => { setFilterRev(""); setFilterCli(""); setFilterProj(""); setSearch(""); setFilterType("Tout"); };

  const marquerToutesLues = async () => {
    try {
      await alertesAPI.markAllRead();
      fetchAlertes();
    } catch (err) {
      console.error("Erreur mark all read:", err);
    }
  };

  const critiques    = alertes.filter(a => a.type === "Critique").length;
  const avertiss     = alertes.filter(a => a.type === "Avertissement").length;
  const infos        = alertes.filter(a => a.type === "Information").length;
  const succes       = alertes.filter(a => a.type === "Succès").length;
  const nonLues      = alertes.filter(a => a.isNew).length;

  const selectStyle = "flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40";

  const typeFilters = [
    { label: t('alerts.filterAll'),      value: "Tout",          count: alertes.length },
    { label: t('alerts.filterCritical'), value: "Critique",      count: critiques },
    { label: t('alerts.filterWarning'),  value: "Avertissement", count: avertiss },
    { label: t('alerts.filterInfo'),     value: "Information",   count: infos },
    { label: t('alerts.filterSuccess'),  value: "Succès",        count: succes },
  ];

  return (
    <>
      {/* Header page */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Bell size={22} className="text-red-500" />
            <h1 className="text-xl font-bold text-slate-800">{t('alerts.title')}</h1>
          </div>
          <p className="text-sm text-slate-400 mt-0.5 ml-7">{t('alerts.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {nonLues > 0 && (
            <button onClick={marquerToutesLues}
              className="px-3 py-1.5 bg-slate-800 text-white text-xs font-semibold rounded-full hover:bg-slate-700 transition-colors">
              {nonLues} {t('alerts.unread')}
            </button>
          )}
          <span className="px-3 py-1.5 border border-gray-200 text-slate-600 text-xs font-semibold rounded-full">
            {alertes.length} {t('alerts.alertsLabel')}
          </span>
        </div>
      </div>

      {/* Filtres cascade */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3">
          {adminMode === 'platform' && (
            <select value={filterRev}  onChange={e => handleRev(e.target.value)} className={selectStyle}>
              <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}
          <select value={filterCli}  onChange={e => handleCli(e.target.value)} className={selectStyle}>
            <option value="">{t('common.allClients')} ({clients.length})</option>
            {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
          </select>
          <select value={filterProj} onChange={e => setFilterProj(e.target.value)} className={selectStyle}>
            <option value="">{t('common.allProjects')} ({projets.length})</option>
            {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search')}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
          {(filterRev || filterCli || filterProj || search) && (
            <button onClick={reset} className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
              <RefreshCw size={14} /> {t('alerts.reset')}
            </button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-5">
        {[
          { label: t('alerts.critical'),  value: critiques, icon: XCircle,       cardBg: "bg-red-50",    numColor: "text-red-600",    iconBg: "bg-red-100",    iconColor: "text-red-500" },
          { label: t('alerts.warnings'),  value: avertiss,  icon: AlertTriangle, cardBg: "bg-yellow-50", numColor: "text-yellow-600", iconBg: "bg-yellow-100", iconColor: "text-yellow-500" },
          { label: t('alerts.info'),      value: infos,     icon: Info,          cardBg: "bg-blue-50",   numColor: "text-blue-600",   iconBg: "bg-blue-100",   iconColor: "text-blue-500" },
          { label: t('alerts.success'),   value: succes,    icon: CheckCircle,   cardBg: "bg-green-50",  numColor: "text-green-600",  iconBg: "bg-green-100",  iconColor: "text-green-500" },
          { label: t('alerts.total'),     value: alertes.length, icon: Activity, cardBg: "bg-gray-50",   numColor: "text-slate-800",  iconBg: "bg-gray-200",   iconColor: "text-slate-500" },
        ].map((s, i) => (
          <div key={i} className={`${s.cardBg} rounded-2xl p-4 border border-gray-100`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-medium ${s.numColor}`}>{s.label}</p>
              <div className={`w-9 h-9 ${s.iconBg} rounded-xl flex items-center justify-center`}>
                <s.icon size={18} className={s.iconColor} />
              </div>
            </div>
            <p className={`text-3xl font-bold ${s.numColor}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filtres par type */}
      <div className="flex items-center gap-2 mb-4">
        {typeFilters.map(f => (
          <button key={f.value}
            onClick={() => setFilterType(f.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filterType === f.value
                ? "bg-slate-800 text-white"
                : "bg-white border border-gray-200 text-slate-600 hover:bg-gray-50"
            }`}
          >
            {f.value === "Critique"      && <XCircle size={13} />}
            {f.value === "Avertissement" && <AlertTriangle size={13} />}
            {f.value === "Information"   && <Info size={13} />}
            {f.value === "Succès"        && <CheckCircle size={13} />}
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste alertes */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-slate-800">{t('alerts.alertList')} ({alertes.length})</h3>
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-3" />
            <p className="text-slate-400 font-medium">{t('alerts.loading')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {alertes.map(a => {
              const style = typeStyles[a.type] || typeStyles["Information"];
              const IconComp = style.icon;
              return (
                <div key={a._id} className={`flex items-start gap-4 px-5 py-4 hover:bg-gray-50 transition-colors ${a.isNew ? "bg-blue-50/30" : ""}`}>
                  {/* Icone */}
                  <div className={`w-10 h-10 ${style.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <IconComp size={18} className={style.iconColor} />
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-semibold text-slate-800 text-sm">{a.label}</p>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${style.badgeBg}`}>
                        {a.type}
                      </span>
                      {a.isNew && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold text-white bg-blue-500">
                          {t('alerts.new')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{a.desc}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Activity size={11} /> {a.sn}
                      </span>
                      <span>• {(typeof a.projet === 'object' ? a.projet?.nom : a.projet) ?? '—'}</span>
                      <span>• {(typeof a.client === 'object' ? a.client?.nom : a.client) ?? '—'}</span>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {formatDate(a.date || a.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Temps relatif */}
                  <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0 mt-1">
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                    </svg>
                    {tempsRelatif(a.date || a.createdAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && alertes.length === 0 && (
          <div className="py-16 text-center">
            <Bell size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">{t('alerts.noAlerts')}</p>
          </div>
        )}
      </div>
    </>
  );
}
